/*
 * Viewing List — shared client-side data layer + UI components
 * Feature: customers add products they'd like to view in person, then
 * submit the list to request an appointment at the showroom.
 *
 * Provides:
 *  - window.ViewingList: localStorage-backed store (add/remove/clear/get)
 *  - <viewing-list-widget>: floating toggle + drawer (rendered globally via theme.liquid)
 *  - <viewing-list-request-form>: selection summary + hidden field sync (Book a Viewing page)
 *  - Global event delegation for any [data-viewing-list-add] button (PDP + product cards)
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'viewing_list_items_v1';
  var DEFAULT_MAX_ITEMS = 20;

  function getMaxItems() {
    var widget = document.querySelector('viewing-list-widget[data-max-items]');
    var value = widget ? Number(widget.getAttribute('data-max-items')) : NaN;
    return Number.isInteger(value) && value > 0 ? value : DEFAULT_MAX_ITEMS;
  }
  var EVENT_NAME = 'viewing-list:updated';

  function readItems() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var items = raw ? JSON.parse(raw) : [];
      return Array.isArray(items) ? items : [];
    } catch (e) {
      return [];
    }
  }

  function writeItems(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      /* localStorage unavailable (private mode / quota) — fail silently */
    }
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { items: items } }));
  }

  function hasItem(id) {
    if (!id) return false;
    return readItems().some(function (i) {
      return i.id === id;
    });
  }

  function addItem(item) {
    var items = readItems();
    if (hasItem(item.id)) {
      return { added: false, reason: 'duplicate', items: items };
    }
    if (items.length >= getMaxItems()) {
      return { added: false, reason: 'limit', items: items };
    }
    item.addedAt = Date.now();
    items.push(item);
    writeItems(items);
    return { added: true, items: items };
  }

  function removeItem(id) {
    var items = readItems().filter(function (i) {
      return i.id !== id;
    });
    writeItems(items);
    return items;
  }

  function clearAll() {
    writeItems([]);
  }

  window.ViewingList = {
    STORAGE_KEY: STORAGE_KEY,
    get MAX_ITEMS() {
      return getMaxItems();
    },
    EVENT_NAME: EVENT_NAME,
    getItems: readItems,
    addItem: addItem,
    removeItem: removeItem,
    clearAll: clearAll,
    hasItem: hasItem
  };

  /* Keep multiple tabs/windows in sync */
  window.addEventListener('storage', function (event) {
    if (event.key === STORAGE_KEY) {
      document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { items: readItems() } }));
    }
  });

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function removeIconSvg() {
    return (
      '<svg viewBox="0 0 20 20" width="14" height="14" fill="none" aria-hidden="true">' +
      '<path d="M5 5L15 15M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '</svg>'
    );
  }

  /* ---------------------------------------------------------------------
   * Add / remove button state (event delegation — works for buttons that
   * are added to the DOM after load, e.g. inside product card grids).
   * ------------------------------------------------------------------- */
  function updateTriggerState(trigger, isAdded) {
    trigger.classList.toggle('is-added', isAdded);
    trigger.setAttribute('aria-pressed', isAdded ? 'true' : 'false');
    var label = trigger.querySelector('[data-viewing-list-label]');
    if (label) {
      label.textContent = isAdded
        ? trigger.getAttribute('data-label-added') || 'Added to viewing list'
        : trigger.getAttribute('data-label-add') || 'Add to viewing list';
    }
  }

  function refreshAllTriggers() {
    var items = readItems();
    var ids = items.map(function (i) {
      return i.id;
    });
    var triggers = document.querySelectorAll('[data-viewing-list-add]');
    for (var i = 0; i < triggers.length; i++) {
      var id = triggers[i].getAttribute('data-product-id');
      updateTriggerState(triggers[i], ids.indexOf(id) !== -1);
    }
  }

  function announce(message) {
    var live = document.querySelector('[data-viewing-list-announce]');
    if (!live || !message) return;
    live.textContent = '';
    window.setTimeout(function () {
      live.textContent = message;
    }, 50);
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-viewing-list-add]');
    if (!trigger) return;
    event.preventDefault();

    var id = trigger.getAttribute('data-product-id');
    if (!id) return;

    if (hasItem(id)) {
      removeItem(id);
      updateTriggerState(trigger, false);
      announce('Removed from viewing list');
      return;
    }

    var item = {
      id: id,
      title: trigger.getAttribute('data-product-title') || '',
      vendor: trigger.getAttribute('data-product-vendor') || '',
      image: trigger.getAttribute('data-product-image') || '',
      url: trigger.getAttribute('data-product-url') || '',
      price: trigger.getAttribute('data-product-price') || '',
      sku: trigger.getAttribute('data-product-sku') || ''
    };

    var result = addItem(item);
    if (result.added) {
      updateTriggerState(trigger, true);
      announce(trigger.getAttribute('data-label-added') || 'Added to viewing list');
    } else if (result.reason === 'limit') {
      announce('Your viewing list is full. Remove an item to add another.');
      window.alert('Your viewing list can hold up to ' + getMaxItems() + ' pieces. Please remove an item before adding another.');
    }
  });

  document.addEventListener(EVENT_NAME, refreshAllTriggers);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshAllTriggers);
  } else {
    refreshAllTriggers();
  }

  /* ---------------------------------------------------------------------
   * <viewing-list-widget> — floating toggle button + drawer
   * ------------------------------------------------------------------- */
  class ViewingListWidget extends HTMLElement {
    connectedCallback() {
      this.toggleBtn = this.querySelector('.viewing-list-widget__toggle');
      this.drawer = this.querySelector('.viewing-list-drawer');
      this.itemsList = this.querySelector('[data-viewing-list-items]');
      this.emptyMessage = this.querySelector('[data-viewing-list-empty]');
      this.countBadge = this.querySelector('[data-viewing-list-count]');
      this.clearBtn = this.querySelector('[data-viewing-list-clear]');
      this.requestLink = this.querySelector('[data-viewing-list-request]');
      this.appointmentUrl = this.getAttribute('data-appointment-url') || '/pages/book-a-viewing';

      if (!this.toggleBtn || !this.drawer) return;

      var self = this;
      this.toggleBtn.addEventListener('click', function () {
        self.open();
      });
      var closeButtons = this.querySelectorAll('[data-viewing-list-close]');
      closeButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.close();
        });
      });
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', function () {
          clearAll();
        });
      }
      this.drawer.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.close();
        if (e.key === 'Tab') self.trapFocus(e);
      });
      if (this.itemsList) {
        this.itemsList.addEventListener('click', function (e) {
          var removeBtn = e.target.closest('[data-viewing-list-remove]');
          if (!removeBtn) return;
          removeItem(removeBtn.getAttribute('data-id'));
        });
      }

      document.addEventListener(EVENT_NAME, function () {
        self.render();
      });
      this.render();
    }

    render() {
      var items = readItems();
      this.toggleBtn.hidden = false;
      if (this.countBadge) {
        this.countBadge.textContent = String(items.length);
        this.countBadge.hidden = items.length === 0;
      }
      if (this.emptyMessage) this.emptyMessage.hidden = items.length !== 0;
      if (this.clearBtn) this.clearBtn.hidden = items.length === 0;

      if (this.itemsList) {
        this.itemsList.innerHTML = '';
        items.forEach(function (item) {
          var li = document.createElement('li');
          li.className = 'viewing-list-drawer__item';
          li.innerHTML =
            '<a href="' + escapeHtml(item.url) + '" class="viewing-list-drawer__item-image" tabindex="-1">' +
            (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="" loading="lazy" width="56" height="56">' : '') +
            '</a>' +
            '<div class="viewing-list-drawer__item-info">' +
            '<a href="' + escapeHtml(item.url) + '" class="viewing-list-drawer__item-title">' + escapeHtml(item.title) + '</a>' +
            (item.price ? '<span class="viewing-list-drawer__item-price">' + escapeHtml(item.price) + '</span>' : '') +
            '</div>' +
            '<button type="button" class="viewing-list-drawer__item-remove" data-viewing-list-remove data-id="' +
            escapeHtml(item.id) +
            '" aria-label="Remove ' +
            escapeHtml(item.title) +
            ' from viewing list">' +
            removeIconSvg() +
            '</button>';
          this.itemsList.appendChild(li);
        }, this);
      }

      if (this.requestLink) {
        this.requestLink.setAttribute('href', this.appointmentUrl);
        if (items.length === 0) {
          this.requestLink.setAttribute('aria-disabled', 'true');
          this.requestLink.classList.add('is-disabled');
        } else {
          this.requestLink.removeAttribute('aria-disabled');
          this.requestLink.classList.remove('is-disabled');
        }
      }
    }

    open() {
      this.drawer.hidden = false;
      var self = this;
      window.requestAnimationFrame(function () {
        self.drawer.classList.add('is-open');
      });
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      this.previouslyFocused = document.activeElement;
      var closeBtn = this.drawer.querySelector('.viewing-list-drawer__close');
      if (closeBtn) closeBtn.focus();
      document.body.classList.add('viewing-list-drawer-open');
    }

    close() {
      var self = this;
      this.drawer.classList.remove('is-open');
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('viewing-list-drawer-open');
      window.setTimeout(function () {
        self.drawer.hidden = true;
      }, 300);
      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.previouslyFocused.focus();
      }
    }

    trapFocus(event) {
      var focusable = this.drawer.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('viewing-list-widget')) {
    customElements.define('viewing-list-widget', ViewingListWidget);
  }

  /* ---------------------------------------------------------------------
   * <viewing-list-request-form> — selection summary on the Book a Viewing page
   * ------------------------------------------------------------------- */
  class ViewingListRequestForm extends HTMLElement {
    connectedCallback() {
      this.itemsList = this.querySelector('[data-vr-items]');
      this.emptyMessage = this.querySelector('[data-vr-empty]');
      this.hiddenField = this.querySelector('[data-vr-items-field]');
      this.dateInput = this.querySelector('#ViewingRequestForm-date');

      var self = this;
      document.addEventListener(EVENT_NAME, function () {
        self.render();
      });
      if (this.itemsList) {
        this.itemsList.addEventListener('click', function (e) {
          var removeBtn = e.target.closest('[data-vr-remove]');
          if (!removeBtn) return;
          removeItem(removeBtn.getAttribute('data-id'));
        });
      }
      if (this.dateInput) {
        var checkWeekend = function () {
          if (!self.dateInput.value) {
            self.dateInput.setCustomValidity('');
            return;
          }
          var parts = self.dateInput.value.split('-');
          var picked = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
          var day = picked.getDay();
          if (day === 0 || day === 6) {
            self.dateInput.setCustomValidity(
              'We are open Monday to Friday only — please choose a weekday.'
            );
            self.dateInput.reportValidity();
          } else {
            self.dateInput.setCustomValidity('');
          }
        };
        this.dateInput.addEventListener('input', checkWeekend);
        this.dateInput.addEventListener('change', checkWeekend);
      }
      this.render();
    }

    render() {
      var items = readItems();

      if (this.emptyMessage) this.emptyMessage.hidden = items.length !== 0;

      if (this.itemsList) {
        this.itemsList.innerHTML = '';
        items.forEach(function (item) {
          var li = document.createElement('li');
          li.className = 'viewing-request-form__item';
          li.innerHTML =
            (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="" loading="lazy" width="56" height="56">' : '<span></span>') +
            '<div class="viewing-request-form__item-info">' +
            (item.url ?
              '<a href="' + escapeHtml(item.url) + '" class="viewing-request-form__item-title">' + escapeHtml(item.title) + '</a>' :
              '<span class="viewing-request-form__item-title">' + escapeHtml(item.title) + '</span>') +
            (item.price ? '<span class="viewing-request-form__item-price">' + escapeHtml(item.price) + '</span>' : '') +
            '</div>' +
            '<button type="button" class="viewing-request-form__item-remove" data-vr-remove data-id="' +
            escapeHtml(item.id) +
            '" aria-label="Remove ' +
            escapeHtml(item.title) +
            '">' +
            removeIconSvg() +
            '</button>';
          this.itemsList.appendChild(li);
        }, this);
      }

      if (this.hiddenField) {
        if (items.length === 0) {
          this.hiddenField.value = '';
        } else {
          var origin = window.location.origin;
          var lines = items.map(function (item, index) {
            var parts = [index + 1 + '. ' + item.title];
            if (item.sku) parts.push('SKU: ' + item.sku);
            if (item.price) parts.push(item.price);
            if (item.url) parts.push(origin + item.url);
            return parts.join(' — ');
          });
          this.hiddenField.value = lines.join('\n');
        }
      }
    }
  }

  if (!customElements.get('viewing-list-request-form')) {
    customElements.define('viewing-list-request-form', ViewingListRequestForm);
  }
})();
